import { FIELD_TYPES } from '../../../types';
import {
  DATA_LOCATION,
  ExtendedTeamData,
  ExtendedTeamPaths,
  PromoFieldData,
  Condition,
  SceneConfig,
  TeamData,
} from './types';
import { MODULE_TYPES } from '../../scenes/scene-config/types';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import { AppConfigData } from '../../../types/app-config';
import { QuerySnapshot } from 'firebase/firestore';

export function databaseUpdateOccurredWith(
  incoming: TeamData | AppConfigData,
  local: TeamData | AppConfigData,
  remote: TeamData | AppConfigData
) {
  // no data has been uploaded to firestore
  const isNone = isEqual(remote, incoming);
  if (isNone) {
    return DATA_LOCATION.NONE;
  }

  // local data has been uploaded to firestore
  const isLocal = isEqual(local, incoming);
  if (isLocal) {
    return DATA_LOCATION.LOCAL;
  }

  // only server changes
  const isRemote = isEqual(local, remote);
  if (isRemote) {
    return DATA_LOCATION.SERVER;
  }

  // server data has been uploaded to firestore
  return DATA_LOCATION.BOTH;
}

export function removeExtendedProps(
  object: ExtendedTeamData,
  paths: ExtendedTeamPaths
): Omit<ExtendedTeamData, ExtendedTeamPaths[0] | ExtendedTeamPaths[1]> {
  const objectClone = cloneDeep(object); // avoid side effects

  return omit(objectClone, paths);
}

export function applyNewFieldValue(
  teamCopy: { [x: string]: any },
  fieldKey: string,
  fieldValue: string | number | boolean | any[] | null
) {
  // splits on "." & "[", removes dots but not brackets
  // example: outer[1].inner -> ["outer", "[1]", "inner"]
  const keys: string[] = fieldKey.split(/(?=\[)|[.]/g);
  const isArray: boolean = keys[1]?.includes('[');

  if (keys.length > 2) {
    if (isArray) {
      const indexOfCurrentArray = keys[1].replaceAll('[', '').replaceAll(']', '');

      // if teamCopy.teamPromos[i] is empty add a placeholder and the current value
      if (!teamCopy[keys[0]][indexOfCurrentArray]) {
        const placeholder = getPlaceholderProp(keys[0]);

        return (teamCopy[keys[0]][indexOfCurrentArray] = {
          ...placeholder,
          ...{ [keys[2]]: fieldValue },
        });
      } else {
        teamCopy[keys[0]][indexOfCurrentArray][keys[2]] = fieldValue;
        // recreate the array with empty items as null
        teamCopy[keys[0]] = Array.from(teamCopy[keys[0]], (element) => {
          return element === undefined ? null : element;
        });
      }
    } else {
      teamCopy[keys[0]][keys[1]][keys[2]] = fieldValue;
    }
  } else if (keys.length > 1) {
    if (isArray) {
      const indexOfCurrentArray = keys[1].replaceAll('[', '').replaceAll(']', '');
      // set value on index, e.g. to be able to set it to null
      teamCopy[keys[0]][indexOfCurrentArray] = fieldValue;
    } else {
      if (teamCopy[keys[0]] === undefined || teamCopy[keys[0]] === null) {
        teamCopy[keys[0]] = {};
      }
      teamCopy[keys[0]][keys[1]] = fieldValue;
    }
  } else {
    teamCopy[keys[0]] = fieldValue;
  }
}

function getPlaceholderProp(currentKey: string) {
  const INITIAL_PLACEHOLDERS = [
    {
      key: 'teamPromos',
      structure: {
        contentUrl: '',
        height: 0,
        promo_age_restriction: false,
        promo_open_inapp: false,
        promo_type: '',
        targetUrl: '',
        width: 0,
      },
    },
    {
      key: 'links',
      structure: {
        imageUrl: '',
        targetUrl: '',
      },
    },
    {
      key: 'info',
      structure: {
        name: '',
        value: '',
      },
    },
  ];

  const currentPlaceholder = INITIAL_PLACEHOLDERS.find((placeholder) => {
    return placeholder.key === currentKey;
  });

  return currentPlaceholder?.structure;
}

export function isAllFieldsValid(fields: { [x: string]: boolean }) {
  return !Object.values(fields).some((value) => !value);
}

// converts data types into a more suitable and TS-friendly format
export function dataTypeConverter(value: string | boolean | number | unknown, type: string) {
  if (type === FIELD_TYPES.IMAGE && typeof value !== 'string') {
    return '';
  }
  if (type === FIELD_TYPES.SWITCH && typeof value !== 'boolean') {
    return false;
  }
  if (typeof value === 'string') {
    return (value as string) || '';
  }
  if (typeof value === 'boolean') {
    return (value as boolean) || false;
  }
  if (typeof value === 'number') {
    return value as number;
  }
  return '';
}

const getPromoFieldData = (sceneConfigCondition: Condition) => {
  const promoIndexes = sceneConfigCondition.modules
    .filter((module) => module.type === MODULE_TYPES.PROMO)
    .map((promoModule) => promoModule.promoIndex);

  const promoFieldData: PromoFieldData = {
    scene: sceneConfigCondition.scene,
    promoIndexes: promoIndexes,
  };

  return promoFieldData;
};

export const convertToPromoField = (
  appConfigsSnapshot: QuerySnapshot<Record<'scene', SceneConfig>[]> | undefined
): PromoFieldData[] => {
  if (typeof appConfigsSnapshot === 'undefined') {
    return [];
  }

  const promoFieldData = appConfigsSnapshot?.docs.flatMap((data) => {
    if (data && Array.isArray(data.get('scene'))) {
      const scenes: SceneConfig[] = data.get('scene');
      return scenes
        .map((scene: SceneConfig) => scene.condition)
        .map((condition: Condition) => getPromoFieldData(condition));
    } else {
      return [];
    }
  });

  return promoFieldData;
};

export const getSceneNames = (promoCollection: PromoFieldData[], moduleIndex: string) =>
  promoCollection.reduce((acc: string[], current) => {
    const sceneNames = [...acc];

    const activePromoIndexes = current.promoIndexes.filter((promo) => promo === moduleIndex);

    if (activePromoIndexes.length) {
      sceneNames.push(current.scene);
    }
    return sceneNames;
  }, []);

export const mergeChanges = (
  incomingData: ExtendedTeamData | AppConfigData | undefined | null,
  remoteDataRef: { current: ExtendedTeamData | AppConfigData | undefined | null },
  localDataRef: { current: ExtendedTeamData | AppConfigData | undefined | null }
) => {
  let updatedDataCopy: ExtendedTeamData | AppConfigData | undefined;
  if (incomingData) {
    remoteDataRef.current = incomingData;
    updatedDataCopy = {
      ...cloneDeep(localDataRef.current),
      ...cloneDeep(incomingData),
    };
    localDataRef.current = updatedDataCopy;
  }
};
